import { useRef, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  LinearProgress,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useQuery } from "react-query";
import useDebounce from "../../../lib/useDebounce";
import axiosClient from "../../../lib/axios";
import {
  ContaContabil,
  ICentroCusto,
} from "../../../types/api/geral/pesquisar-plano-contas";
import LoadingApiQuery from "../../LoadingApiQuery";
import { OnChangeValue, TExtraParams, Value } from ".";
import DialogCentroCusto from "./DialogCentroCusto";

export default function DialogSelectItems({
  open,
  handleClose,
  title,
  defValue,
  onSubmitValue,
  extraParams,
  hasCentroCusto = true,
}: DialogSelectItemsProps) {
  const [keyword, setKeyword] = useState<string>("");
  const [selected, setSelected] = useState<Value>(defValue);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [limit, setLimit] = useState<number>(50);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  //queries e mutations
  const debouncedKeyword = useDebounce(keyword);
  const { data, isFetching, isLoading } = useQuery({
    queryKey: [
      "/contabilidade/plano-contas/pesquisar-plano-contas",
      debouncedKeyword,
      limit,
    ],
    queryFn: () =>
      axiosClient.get("/contabilidade/plano-contas/pesquisar-plano-contas", {
        params: {
          page: 1,
          limit: limit,
          pesquisar: debouncedKeyword,
          ...extraParams,
        },
      }),
    retry: false,
    keepPreviousData: true,
    cacheTime: 0,
  });
  const dadosContabil: ContaContabil[] = data?.data.data || [];
  //

  //funções manipuladoras
  const handleSubmit = () => {
    if (selected) {
      onSubmitValue(selected);
      handleClose();
    }
  };

  const contarPontos = (str: string) => {
    return str.split(".").length - 1;
  };

  const handleSelecionarCentroCusto = (valorSelect: ICentroCusto) => {
    if (selected) {
      const param = {
        ...selected,
        centro_custo: valorSelect,
      };
      onSubmitValue(param);
      handleClose();
    }
  };

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const bottom =
      event.currentTarget.scrollHeight - event.currentTarget.scrollTop ===
      event.currentTarget.clientHeight;
    if (bottom && !isFetching) {
      setLimit((prevLimit) => prevLimit + 20);
    }
  };
  //

  //valores importantes
  const bloquearConfirmar = !selected;
  //

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        scroll="body"
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>Selecionar {title}</DialogTitle>

        <DialogContent>
          <Box py={2}>
            <TextField
              fullWidth
              value={keyword}
              label="Pesquisar"
              onChange={({ target }) => setKeyword(target.value)}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }
              }}
            />

            {isLoading && (
              <LoadingApiQuery
                extraParams={{ height: 400, my: 1, alignItems: "center" }}
              />
            )}

            <Box mt={1}>{isFetching && data && <LinearProgress />}</Box>
            {!isLoading && data && (
              <TableContainer
                ref={tableContainerRef}
                onScroll={handleScroll}
                sx={{ height: 400, overflow: "auto", mb: 1 }}
              >
                <Table size="small" aria-label="a dense table" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell></TableCell>
                      <TableCell>
                        <b>Hierarquico</b>
                      </TableCell>
                      <TableCell>
                        <b>Nome</b>
                      </TableCell>
                      <TableCell>
                        <b>Código</b>
                      </TableCell>
                      <TableCell>
                        <b>D/C</b>
                      </TableCell>
                      <TableCell>
                        <b>Sistema XY</b>
                      </TableCell>
                      <TableCell>
                        <b>Tipo</b>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dadosContabil.map((row) => {
                      const identificador = row.id.toString();
                      const radioMarcado =
                        selected?.codigo_contabil === row.codigo_contabil;
                      const qtdPontos = contarPontos(
                        row.conta_contabil_formatada
                      );

                      return (
                        <TableRow
                          key={row.id}
                          sx={{
                            cursor: "pointer",
                            backgroundColor: radioMarcado ? "#edf7ed" : "unset",
                            "&:last-child td, &:last-child th": { border: 0 },
                          }}
                          onClick={() => {
                            if (row.centro_custos) setOpenModal(true);
                            setSelected(row);
                          }}
                        >
                          <TableCell>
                            <Radio
                              name="radio-buttons"
                              checked={radioMarcado}
                              slotProps={{ input: { 'aria-label': identificador } }}
                            />
                          </TableCell>
                          <TableCell>{row.conta_contabil_formatada}</TableCell>
                          <TableCell>
                            <Typography
                              variant="subtitle2"
                              sx={{ pl: `${qtdPontos * 12}px` }}
                            >
                              {row.nome}
                            </Typography>
                          </TableCell>
                          <TableCell>{row.codigo_contabil}</TableCell>
                          <TableCell>{row.natureza}</TableCell>
                          <TableCell>{row.sistema_xy || "-"}</TableCell>
                          <TableCell>{row.tipo || "-"}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button
            autoFocus
            variant="contained"
            onClick={handleSubmit}
            disabled={bloquearConfirmar}
          >
            Selecionar
          </Button>
        </DialogActions>
      </Dialog>

      {openModal && selected && hasCentroCusto && (
        <DialogCentroCusto
          open={openModal}
          opcoes={"centro_custos" in selected ? selected.centro_custos : []}
          handleClose={() => {
            setOpenModal(false);
            setSelected(null);
          }}
          handleSubmit={handleSelecionarCentroCusto}
        />
      )}
    </>
  );
}

interface DialogSelectItemsProps {
  open: boolean;
  handleClose: () => void;
  title: string;
  defValue: Value;
  onSubmitValue: OnChangeValue;
  extraParams?: TExtraParams;
  hasCentroCusto?: boolean;
}
